import { createAsyncThunk, createSlice, SerializedError } from '@reduxjs/toolkit';
import { RouteDefinition } from 'smash-framework/dist/middlewares/apiGatewayProxyRest/interfaces';
import { RootState } from '../../store';
import { sortApiVersions } from './helpers/apiVersion';
import { mapResourcesAndRoutes } from './helpers/route';

export interface ApiConfiguration {
    [key: string]: {
        latestVersion: string;
        versions: { [key: string]: string };
    }
};

export interface ApiReferenceState {
    references: {
        isLoading: boolean;
        isError: boolean;
        isSuccess: boolean;
        routes?: { [key: string]: RouteDefinition[] };
        availableVersions?: string[];
        latestVersion?: string;
        error?: SerializedError;
    };
    apiConfiguration: {
        isLoading: boolean;
        isError: boolean;
        isSuccess: boolean;
        error?: SerializedError;
        config?: ApiConfiguration;
    };
}

export const loadApiConfiguration = createAsyncThunk(
    'apiReference/loadApiConfiguration',
    async () => {
        const configApi = await fetch(`${window.location.origin}/apiDoc/config.json`);
        const config = await configApi.json();
        return config;
    }
)

export const listServiceVersionActions = createAsyncThunk(
    'apiReference/listServiceVersionActions',
    async ({ service, version }: { service: string; version?: string }, thunkApi) => {
        try {
            const configApi = await fetch(`${window.location.origin}/apiDoc/config.json`);
            const config = await configApi.json();
            const sortedVersions = sortApiVersions(config[service].versions);
            const latestVersion = config[service].latestVersion;
            const versionToUse = version || latestVersion;
            const correspondingFilePath = `${service}.${versionToUse}.apidoc.json`;
            if (!correspondingFilePath) {
                return thunkApi.rejectWithValue(new Error(`No API reference found for ${service}`));
            }
            const api = await fetch(`${window.location.origin}/apiDoc/${correspondingFilePath}`);
            const apiJson = await api.json();
            const routes = apiJson.routes as RouteDefinition[];

            const mapResourceRoutes = mapResourcesAndRoutes(routes);
            
            return {
                mapResourceRoutes,
                availableVersions: sortedVersions,
                latestVersion,
            };
        } catch (error) {
            return thunkApi.rejectWithValue(error as SerializedError);
        }
    }
)

export const initialState: ApiReferenceState = {
    references: {
        isLoading: false,
        isError: false,
        isSuccess: false,
        routes: undefined,
        availableVersions: undefined,
        error: undefined
    },
    apiConfiguration: {
        isLoading: false,
        isError: false,
        isSuccess: false,
        config: undefined,
        error: undefined
    },
}

export const apiReferenceSlice = createSlice({
    name: 'apiReference',
    initialState,
    reducers: {
        reset: (state) => {
            state.references = initialState.references;
        }
    },
    extraReducers: (builder) => {
        builder
            // loadApiConfiguration
            .addCase(loadApiConfiguration.pending, (state) => {
                state.apiConfiguration.isLoading = true;
                state.apiConfiguration.isError = false;
            })
            .addCase(loadApiConfiguration.fulfilled, (state, action) => {
                state.apiConfiguration.isLoading = false;
                state.apiConfiguration.isSuccess = true;
                state.apiConfiguration.config = action.payload;
            })
            .addCase(loadApiConfiguration.rejected, (state, action) => {
                state.apiConfiguration.isLoading = false;
                state.apiConfiguration.isError = true;
                state.apiConfiguration.error = action.payload as SerializedError;
            })
            // listServiceVersionActions
            .addCase(listServiceVersionActions.pending, (state) => {
                state.references.isLoading = true;
                state.references.isError = false;
            })
            .addCase(listServiceVersionActions.fulfilled, (state, action) => {
                state.references.isLoading = false;
                state.references.isSuccess = true;
                state.references.routes = action.payload.mapResourceRoutes;
                state.references.availableVersions = action.payload.availableVersions;
                state.references.latestVersion = action.payload.latestVersion;
            })
            .addCase(listServiceVersionActions.rejected, (state, action) => {
                state.references.isLoading = false;
                state.references.isError = true;
                state.references.routes = undefined;
                state.references.availableVersions = undefined;
                state.references.latestVersion = undefined;
                state.references.error = action.payload as SerializedError;
            })
    }
});

export const { reset } = apiReferenceSlice.actions;
export const apiReferenceState = (state: RootState) => state.apiReference;

export default apiReferenceSlice.reducer;